import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import useTheme from "../ColorConfig/usetheme";
import RollingCounter from "./SubComponents/RollingCounter";
import CountryAnimation from "./SubComponents/CountryAnimation";
import ClientAnimation from "./SubComponents/ClientAnimation";
import LiveIndicator from "./SubComponents/Live";
import StatCard from "./SubComponents/StatCard";
import HeaderSection from "./SubComponents/HeaderSec";
import { fetchStatsData, getIconForStat, shouldShowCountryAnimation, shouldShowClientAnimation, shouldShowLiveIndicator, isSpecialDisplayCard, getSpecialDisplayText } from "./SubComponents/StatsUtils";

const StatsSection = () => {
  const colors = useTheme();
  const [autoAnimated, setAutoAnimated] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [cardsAnimated, setCardsAnimated] = useState([]);
  const [statsData, setStatsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const sectionRef = useRef(null);

  const handlePhoneClick = () => {
    window.location.href = 'tel:+919840841840';
  };

  useEffect(() => {
    fetchStatsData({
      setStatsData,
      setLoading,
      setError,
      getIconForStat,
      shouldShowCountryAnimation,
      shouldShowClientAnimation,
      shouldShowLiveIndicator,
      isSpecialDisplayCard,
      getSpecialDisplayText
    });
  }, []);

  useEffect(() => {
    if (autoAnimated || loading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !autoAnimated && !loading) {
            setIsVisible(true);
            setTimeout(() => {
              setAutoAnimated(true);
              animateCardsOneByOne();
            }, 800);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [autoAnimated, loading]);

  const animateCardsOneByOne = () => {
    statsData.forEach((_, index) => {
      setTimeout(() => {
        setCardsAnimated(prev => [...prev, index]);
      }, index * 150);
    });
  };

  if (loading) {
    return <LoadingState colors={colors} />;
  }

  return (
    <div className="stats-wrapper" ref={sectionRef}>
      <div className={`stats-section ${isVisible ? 'animate-in' : ''}`}>
        <div className="container">
          <HeaderSection 
            colors={colors} 
            error={error} 
            isVisible={isVisible} 
          />
          
          <div className="stats-grid">
            {statsData.map((stat, index) => (
              <StatCard
                key={stat.id}
                stat={stat}
                index={index}
                colors={colors}
                isCardAnimated={cardsAnimated.includes(index)}
                isHovered={hoveredCard === stat.id}
                autoAnimated={autoAnimated}
                onMouseEnter={() => setHoveredCard(stat.id)}
                onMouseLeave={() => setHoveredCard(null)}
                onClick={stat.isClickable ? handlePhoneClick : undefined}
              />
            ))}
          </div>
        </div>
      </div>
      <Styles colors={colors} />
    </div>
  );
};

const LoadingState = ({ colors }) => (
  <div className="stats-wrapper">
    <div className="stats-section">
      <div className="container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p style={{ color: colors.white, marginTop: '20px' }}>Loading statistics...</p>
        </div>
      </div>
    </div>
    <Styles colors={colors} />
  </div>
);

const Styles = ({ colors }) => (
  <style jsx>{`
        .stats-wrapper {
          background: ${colors.bodyBlack};
          padding: 20px 0;
          position: relative;
          overflow: hidden;
        }

        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 10px;
          position: relative;
          z-index: 1;
        }

        .stats-section {
          opacity: 0;
          transform: translateY(50px);
          transition: all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          margin-bottom: 60px;
        }

        .stats-section.animate-in {
          opacity: 1;
          transform: translateY(0);
        }

        .header-section {
          text-align: center;
          margin-bottom: 30px;
        }

        .subtitle {
          font-size: 1.1rem;
          max-width: 800px;
          margin: 0 auto;
          line-height: 1.6;
          margin-bottom: 60px;
        }

        .error-message {
          margin-top: 10px;
        }

        .stats-grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-items: center;
          gap: 60px;
          max-width: 1500px;
          margin: 0 auto;
          margin-bottom: 20px;
        }

        .stat-card {
          backdrop-filter: blur(10px);
          border-radius: 16px;
          padding: 15px 15px;
          text-align: center;
          position: relative;
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          opacity: 0;
          transform: translateY(100px);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-width: 200px;
          overflow: visible;
          cursor: default;
        }

        .clickable-card {
          cursor: pointer;
        }

        .card-hovered {
          transform: translateY(-8px) scale(1.02);
        }
        
        .counter-number {
          display: inline-block;
          font-size: 2rem;
          font-weight: 800;
          color: ${colors.white};
          transition: all 0.2s ease;
        }
        
        .counter-number.counting {
          animation: pulse 0.5s ease-in-out infinite alternate;
        }

        .stat-card.animate-card {
          opacity: 1;
          transform: translateY(0);
          transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        .stat-card:hover:not(.clickable-card) {
          transform: translateY(-8px) scale(1.02);
        }

        .stat-card:hover .stat-icon {
          color: ${colors.primary};
          transform: scale(1.2);
        }

        .stat-icon-wrapper {
          position: relative;
          display: inline-block;
          margin-bottom: 15px;
          transition: transform 0.3s ease;
        }

        .stat-icon {
          color: ${colors.white};
          font-size: 32px;
          transition: all 0.3s ease;
        }

        .phone-overlay {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          background: rgba(255, 255, 255, 1);
          border-radius: 50%;
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: all 0.3s ease;
        }

        .phone-icon {
          color: #22c55e;
          font-size: 16px;
        }

        .clickable-card:hover .phone-overlay {
          opacity: 1;
        }

        .stat-card:hover .stat-icon-wrapper {
          transform: translateY(-5px);
        }

        .stat-number {
          margin-bottom: 10px;
        }

        .rolling-counter {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          font-weight: 800;
          color: ${colors.white};
        }

        .plus {
          color: ${colors.primary};
          margin-left: 3px;
          font-size: 1.5rem;
          font-weight: 700;
        }

        @keyframes pulse {
          from { transform: scale(1); }
          to { transform: scale(1.05); }
        }

        .support-text {
          font-size: 2rem;
          font-weight: 800;
          color: ${colors.white};
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .stat-title {
          font-size: 0.9rem;
          font-weight: 600;
          color: ${colors.white};
          margin: 0;
          line-height: 1.3;
        }

        /* Live Indicator Styles */
        .live-indicator {
          position: absolute;
          top: 28px;
          right: 25px;
          display: flex;
          align-items: center;
          gap: 4px;
          background: #22c55e;
          padding: 2px 4px;
          border-radius: 5px;
          z-index: 20;
          box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
        }

        .live-pulse {
          width: 7px;
          height: 7px;
          background: white;
          border-radius: 50%;
          animation: livePulse 2s infinite;
        }

        .live-text {
          font-size: 0.6rem;
          font-weight: 700;
          color: white;
          letter-spacing: 0.5px;
        }

        @keyframes livePulse {
          0% {
            transform: scale(0.8);
            opacity: 1;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.7;
          }
          100% {
            transform: scale(0.8);
            opacity: 1;
          }
        }

        /* Country & Client Badge Styles */
        .country-badge,
        .client-badge {
          position: absolute;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 8px;
          z-index: 10;
          min-width: 80px;
        }

        .country-badge.top-right,
        .client-badge.top-right {
          top: -20px;
          right: -20px;
        }

        .country-badge.top-left,
        .client-badge.top-left {
          top: -20px;
          left: -20px;
        }

        .country-badge.bottom-left,
        .client-badge.bottom-left {
          bottom: -30px;
          left: -30px;
        }

        .country-badge.bottom-right,
        .client-badge.bottom-right {
          bottom: -30px;
          right: -30px;
        }

        .country-content {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 6px;
          margin-bottom: 4px;
        }

    .client-logo-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: #ebebebff; /* White background for the entire container */
  border-radius: 12px; /* Rounded corners for the container */
  padding: 5px 5px 5px 5px; /* More padding at top, less at bottom */
  // box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3); /* Enhanced shadow */
  // gap: 1px; /* Space between logo and underline */
}

        .country-flag-img {
          width: 20px;
          height: 15px;
          border-radius: 2px;
          object-fit: cover;
        }

    .client-logo-img {
  width: 40px; /* Increased logo size */
  height: 40px; /* Increased logo size */
  border-radius: 8px;
  object-fit: contain;
  /* Removed margin-bottom since we're using gap */
}
        .country-name {
          font-size: 0.7rem;
          font-weight: 600;
          color: ${colors.white};
          text-align: center;
          letter-spacing: 0.5px;
        }

        .underline {
          width: 25px;
          height: 2px;
          background: ${colors.primary};
          border-radius: 1px;
        }
    .underline1 {
  width: 25px;
  height: 2px; /* Slightly thicker underline */
  background: var(--color-primary); /* Using your primary color */
  border-radius: 2px;
  margin-top: -2px; /* Small additional spacing */
}

        /* Responsive Design */
        @media (max-width: 1200px) {
          .stats-grid {
            gap: 30px;
          }
          
          .stat-card {
            min-width: 180px;
          }
        }

        @media (max-width: 768px) {
          .stats-wrapper {
            padding: 60px 0;
          }
          
          .achievement-title {
            font-size: 1.8rem; 
          }

          .stats-grid {
            gap: 30px;
          }

          .stat-card {
            padding: 20px 10px;
            min-width: 140px;
          }

          .rolling-counter {
            font-size: 1.5rem;
          }

          .support-text {
            font-size: 1.5rem;
          }

          .subtitle {
            font-size: 1rem;
            padding: 0 10px;
          }

          .live-indicator {
            top: -6px;
            right: -6px;
            padding: 3px 6px;
          }

          .live-text {
            font-size: 0.6rem;
          }

          .live-pulse {
            width: 6px;
            height: 6px;
          }

          .country-badge,
          .client-badge {
            padding: 6px;
            min-width: 70px;
          }

          .country-flag-img {
            width: 18px;
            height: 13px;
          }

          .client-logo-img {
            width: 35px; /* Adjusted for mobile */
            height: 35px; /* Adjusted for mobile */
            padding: 3px;
          }

          .country-name {
            font-size: 0.65rem;
          }

          .underline {
            width: 20px;
          }
        }

        @media (max-width: 480px) {
          .stats-wrapper {
            padding: 20px 0;
          }
          
          .stats-grid {
            gap: 70px;
          }

          .stat-card {
            min-width: 120px;
            padding: 1px 8px;
          }

          .rolling-counter {
            font-size: 1.3rem;
          }

          .support-text {
            font-size: 1.3rem;
          }

          .stat-title {
            font-size: 0.85rem;
          }
          
          .header-section {
            margin-bottom: 40px;
          }

          .live-indicator {
            top: 17px;
            right: -4px;
            padding: 2px 4px;
          }

          .live-text {
            font-size: 0.5rem;
          }

          .live-pulse {
            width: 4px;
            height: 4px;
          }

          .country-badge,
          .client-badge {
            padding: 4px;
            min-width: 60px;
          }

          .country-badge.top-right,
          .client-badge.top-right {
            top: -25px;
            right: -15px;
          }

          .country-badge.top-left,
          .client-badge.top-left {
            top: -25px;
            left: -15px;
          }

          .country-badge.bottom-left
          {
            bottom: -45px;
            left: -15px;
          }
             
          .client-badge.bottom-left {
            bottom: -55px;
            left: -15px;
          }

          
          .client-badge.bottom-right {
            bottom: -55px;
            right: -15px;
          }

           .country-badge.bottom-right
           {
            bottom: -45px;
            right: -15px;
          }

          .country-flag-img {
            width: 16px;
            height: 12px;
          }

          .client-logo-img {
            width: 30px; /* Adjusted for small mobile */
            height: 30px; /* Adjusted for small mobile */
            padding: 2px;
          }

          .country-name {
            font-size: 0.6rem;
          }

          .underline {
            width: 18px;
          }
        }
      `}</style>
);

export default StatsSection;